// References:
// * https://github.com/epicweb-dev/epic-stack/blob/30a4a3ae76c41e922ad452ca26d0d81c5e9d47e8/app/root.tsx
// * https://github.com/shadcn-ui/ui/blob/79c054ac7a7b2a13acc09d72106fd40663cc9bee/apps/www/app/layout.tsx

import {
  Links,
  type LinksFunction,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
} from "react-router";
import { HoneypotProvider } from "remix-utils/honeypot/react";

import { getPublicEnv } from "@/site/env/public-env.server";
import { authenticationEnabled } from "@/site/env/schema.server";
import styleSheet from "@/site/ui/tailwind.css?url";
import { getColorTheme } from "@/theme/color-theme.server";
import { useColorTheme, useTheme } from "@/theme/theme";
import { getTheme } from "@/theme/theme.server";
import { Toaster } from "@/ui/sonner";
import { UmamiAnalytics } from "@/umami/analytics";
import { getUmamiConfig } from "@/umami/config.server";
import { ClientHintCheck, getHints } from "@/utils/client-hints";
import { combineHeaders } from "@/utils/header.server";
import { honeypot } from "@/utils/honeypot.server";
import { useNonce } from "@/utils/nonce";
import { createSessionMiddleware } from "@/utils/session-middleware.server";
import { makeTimings } from "@/utils/timing.server";

import type { Route } from "./+types/root";

export const middleware: Route.MiddlewareFunction[] = [
  createSessionMiddleware({ authenticationEnabled }),
];

export const links: LinksFunction = () =>
  [
    // Apple touch icon.
    //
    {
      rel: "apple-touch-icon",
      sizes: "180x180",
      href: "/favicons/apple-touch-icon-light.png",
      media: "(prefers-color-scheme: light)",
    },
    {
      rel: "apple-touch-icon",
      sizes: "180x180",
      href: "/favicons/apple-touch-icon-dark.png",
      media: "(prefers-color-scheme: dark)",
    },

    // PNG favicons.
    //
    {
      rel: "icon",
      type: "image/png",
      sizes: "16x16",
      href: "/favicons/favicon-16x16-light.png",
      media: "(prefers-color-scheme: light)",
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "16x16",
      href: "/favicons/favicon-16x16-dark.png",
      media: "(prefers-color-scheme: dark)",
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "32x32",
      href: "/favicons/favicon-32x32-light.png",
      media: "(prefers-color-scheme: light)",
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "32x32",
      href: "/favicons/favicon-32x32-dark.png",
      media: "(prefers-color-scheme: dark)",
    },

    // ICO favicons.
    //
    // Place themed favicons last in the links array to give them the highest
    // priority, with a generic fallback first for maximum compatibility.
    //
    { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
    {
      rel: "icon",
      href: "/favicon-light.ico",
      media: "(prefers-color-scheme: light)",
    },
    {
      rel: "icon",
      href: "/favicon-dark.ico",
      media: "(prefers-color-scheme: dark)",
    },

    { rel: "manifest", href: "/site.webmanifest" },

    { rel: "stylesheet", href: styleSheet },
  ].filter(Boolean);

export async function loader({ request }: Route.LoaderArgs) {
  const honeyProps = await honeypot.getInputProps();
  const requestUrl = new URL(request.url);
  const timings = makeTimings("root loader");

  return Response.json(
    {
      requestInfo: {
        hints: getHints(request),
        path: requestUrl.pathname,
        userPreference: {
          colorTheme: getColorTheme(request),
          theme: getTheme(request),
        },
      },
      publicEnv: getPublicEnv(),
      umamiConfig: getUmamiConfig(requestUrl),
      honeyProps,
    },
    {
      headers: combineHeaders({ "Server-Timing": timings.toString() }),
    },
  );
}

function App() {
  const data = useLoaderData<typeof loader>();
  const nonce = useNonce();
  const colorTheme = useColorTheme();
  const theme = useTheme() ?? "light";

  return (
    <html className={`${theme}`} data-color-theme={colorTheme} lang="en">
      <head>
        <ClientHintCheck nonce={nonce} />
        <Meta />
        <meta charSet="utf-8" />
        <meta content="width=device-width,initial-scale=1" name="viewport" />
        {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          data.publicEnv.allowIndexing ? null : (
            <meta content="noindex, nofollow" name="robots" />
          )
        }
        <Links />
        <UmamiAnalytics nonce={nonce} {...data.umamiConfig} />
      </head>

      <body className="bg-background font-sans antialiased">
        <Outlet />
        <Toaster
          closeButton
          richColors
          position="bottom-center"
          theme={theme}
        />
        <script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.publicEnv = ${JSON.stringify(data.publicEnv)}`,
          }}
        />
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
      </body>
    </html>
  );
}

export default function AppWithProviders() {
  const data = useLoaderData<typeof loader>();

  return (
    <HoneypotProvider {...data.honeyProps}>
      <App />
    </HoneypotProvider>
  );
}
